import CalendarSharingDeliveryOption from '../store/schema/CalendarSharingDeliveryOption';
import CalendarSharingError from '../store/schema/CalendarSharingError';
import SharingPermissionItemState from '../store/schema/SharingPermissionItemState';
import { action } from 'satcheljs';

// This action is listened by both orchestrator and mutator and takes folderId
export let showCalendarSharingPermissionsDialog = action(
    'showCalendarSharingPermissionsDialog',
    (folderId: string, calendarId: string, calendarHexColor: string, calendarTitle: string) => ({
        folderId,
        calendarId,
        calendarHexColor,
        calendarTitle,
        datapointConfig: {
            name: 'ShowCalendarSharingDialog',
            customData: { CalendarId: folderId },
        },
    })
);

export let hideCalendarSharingPermissionsDialog = action('hideCalendarSharingPermissionsDialog');

// Mutator listens for this action to set store with initial data
export let saveInitialCalendarSharingPermissions = action(
    'saveInitialCalendarSharingPermissions',
    (
        peopleInsideOrg: SharingPermissionItemState[],
        peopleOutsideOrg: SharingPermissionItemState[],
        delegates: SharingPermissionItemState[],
        currentDeliveryOption: CalendarSharingDeliveryOption
    ) => ({
        peopleInsideOrg,
        peopleOutsideOrg,
        delegates,
        currentDeliveryOption,
    })
);

// Action to trigger orchestrator for making web-service call to load sharing permissions data for a person
export let loadCalendarSharingPermissionInfoDialog = action(
    'loadCalendarSharingPermissionInfoDialog',
    (folderId: string, emailAddress: string) => ({ folderId, emailAddress })
);

// Mutator listens for this action to store the sharing permission info
export let saveCalendarSharingPermissionForSelectedPerson = action(
    'saveCalendarSharingPermissionForSelectedPerson',
    (recipient: SharingPermissionItemState) => ({ recipient })
);

// Action for orchestrator to trigger web-service call for sending the share invite.
export let sendCalendarSharingInvite = action(
    'sendCalendarSharingInvite',
    (
        folderId: string,
        emailAddress?: string,
        detailLevel?: string,
        viewPrivateAppointments?: boolean,
        displayName?: string
    ) => ({
        folderId,
        emailAddress,
        detailLevel,
        viewPrivateAppointments,
        displayName,
        datapointConfig: {
            name: 'SendCalendarSharingInvite',
            customData: {
                CalendarId: folderId,
                PermissionLevel: detailLevel,
                ViewPrivateAppointments: viewPrivateAppointments,
            },
        },
    })
);

// Action for orchestrator to trigger web-service call for deleting the sharing permission.
export let deleteCalendarSharingPermission = action(
    'deleteCalendarSharingPermission',
    (folderId: string, recipient: SharingPermissionItemState) => ({
        folderId,
        recipient,
        datapointConfig: {
            name: 'DeleteCalendarSharingInvite',
            customData: { CalendarId: folderId },
        },
    })
);

// Action to trigger orchestrator for making web-service call to update delivery option.
export let updateDelegateSharingPermission = action(
    'updateDelegateSharingPermission',
    (folderId: string, selectedDeliveryOption: CalendarSharingDeliveryOption) => ({
        folderId,
        selectedDeliveryOption,
    })
);

// Action for orchestrator to trigger web-service call for modifying the sharing permission.
export let updateCalendarSharingInvite = action(
    'updateCalendarSharingInvite',
    (
        folderId: string,
        recipient: SharingPermissionItemState,
        emailAddress?: string,
        detailLevel?: string,
        viewPrivateAppointments?: boolean,
        displayName?: string
    ) => ({
        folderId,
        recipient,
        emailAddress,
        detailLevel,
        viewPrivateAppointments,
        displayName,
        datapointConfig: {
            name: 'UpdateCalendarSharingInvite',
            customData: {
                CalendarId: folderId,
                DetailLevel: detailLevel,
                ViewPrivateAppointments: viewPrivateAppointments,
            },
        },
    })
);

// Action for mutator to add sharing permission in store.
export let addCalendarSharingPermission = action(
    'addCalendarSharingPermission',
    (sharingPermission: SharingPermissionItemState) => ({ sharingPermission })
);

// Action for mutator to update sharing permission in store.
export let updateCalendarSharingPermissionInStore = action(
    'updateCalendarSharingPermission',
    (sharingPermission: SharingPermissionItemState) => ({ sharingPermission })
);

export let updateCurrentDeliveryOptionInStore = action(
    'updateCurrentDeliveryOptionInStore',
    (currentDeliveryOption: CalendarSharingDeliveryOption) => ({ currentDeliveryOption })
);

// Action for mutator to remove sharing permission in store.
export let removeCalendarSharingPermission = action(
    'removeCalendarSharingPermission',
    (sharingPermission: SharingPermissionItemState) => ({ sharingPermission })
);

// Action for mutator to set isSendInvitePending for spinner.
export let setSendInvitePending = action(
    'setSendInvitePending',
    (isSendInvitePending: boolean) => ({
        isSendInvitePending,
    })
);

// Action for mutator to set isDeliveryOptionUpdatePending for spinner.
export let setDeliveryOptionUpdatePending = action(
    'setDeliveryOptionUpdatePending',
    (isDeliveryOptionUpdatePending: boolean) => ({
        isDeliveryOptionUpdatePending,
    })
);

export let setInitialSharingPermissionsPending = action(
    'setInitialSharingPermissionsPending',
    (isPending: boolean) => ({
        isPending,
    })
);

export let setSharingPermissionPending = action(
    'setSharingPermissionPending',
    (sharingPermission: SharingPermissionItemState, isPending: boolean) => ({
        sharingPermission,
        isPending,
    })
);

// Action for mutator to update error status in store.
export let setSharingPermissionError = action(
    'setSharingPermissionError',
    (sharingPermission: SharingPermissionItemState, errorStatus: CalendarSharingError) => ({
        sharingPermission,
        errorStatus,
    })
);

// Action for mutator to remove sharing permission in store.
export let removeCalendarInvitePermission = action(
    'removeCalendarInvitePermission',
    (sharingPermission: SharingPermissionItemState) => ({ sharingPermission })
);

// Action for mutator to remove sharing permission in store.
export let updateSelectedRecipientInvite = action(
    'updateSelectedRecipientInvite',
    (sharingPermission: SharingPermissionItemState, ddValue: string, checked: boolean) => ({
        sharingPermission,
        ddValue,
        checked,
    })
);

//Action for mutator to set isPeoplePickerPermissionPending
export let setPeoplePickerPermissionPending = action(
    'setPeoplePickerPermissionPending',
    (isPending: boolean, email: string) => ({
        isPending,
        email,
    })
);

//Action for mutator to set errorStatus
export let setCalendarSharingError = action(
    'setCalendarSharingError',
    (errorStatus: CalendarSharingError) => ({
        errorStatus,
    })
);

//Action for mutator to set initial load failure status
export let setCalendarSharingInitialLoadError = action(
    'setCalendarSharingInitialLoadError',
    (isInitialLoadError: boolean) => ({
        isInitialLoadError,
    })
);

//Action to reset UserActionAriaLabel property
export let resetUserActionAriaLabel = action('resetUserActionAriaLabel');
